import { useRef } from 'react';

import { useAddAutoshipSubscription } from '@/entities/autoship-subscription';
import { clearErrors, handleSubmit } from '@/entities/purchase-form';

import { useTrackEventAddToCart } from '../analytics';
import { useOnSuccessExistingAutoship } from '../success';
import { useAddToCartOnError } from '../useAddToCartOnError';
import { useNormalizeAutoshipSubscriptionData } from '../useNormalizeAutoshipSubscriptionData';

export function useExistingAutoshipSubmit() {
    const submitPromiseRef = useRef<Promise<void> | null>(null);
    const { trackEvent } = useTrackEventAddToCart();
    const normalizeAutoshipSubscriptionData = useNormalizeAutoshipSubscriptionData();

    const { onAddToCartError: onError } = useAddToCartOnError();
    const { onSuccess } = useOnSuccessExistingAutoship();

    const { mutateAsync: addAutoshipSubscriptionMutation } = useAddAutoshipSubscription({ onError, onSuccess });

    const submitHandler = handleSubmit(async data => {
        if (submitPromiseRef.current) {
            await submitPromiseRef.current;
            return;
        }

        const submitPromise = (async () => {
            clearErrors();

            const normalizedData = normalizeAutoshipSubscriptionData(data);
            if (!normalizedData) {
                return;
            }

            trackEvent();

            await addAutoshipSubscriptionMutation(normalizedData);
        })();

        submitPromiseRef.current = submitPromise;

        try {
            await submitPromise;
        } finally {
            if (submitPromiseRef.current === submitPromise) {
                submitPromiseRef.current = null;
            }
        }
    });

    return { submitHandler };
}
